import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Col, Form, InputNumber, Row, Select, Input, AutoComplete, Card, Typography, Modal } from "antd";
import { useRef, useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { globalProductSearch } from "../../redux/actions/product/globalSearchAction";
import { debounce } from "../../utils/debounce";
import AddProd from "../product/addProd";
import ProductSearchModal from "../sale/ProductSearchModal";

const { Text } = Typography;

const getItemTotal = (item) => {
  if (!item || typeof item !== "object") return 0;
  var totalPrice = item.product_quantity * item.product_sale_price;
  if (isNaN(totalPrice)) totalPrice = 0;
  return totalPrice;
};

export default function Products({
  allProducts,
  formData,
  updateFormData,
  selectedProds,
  handleSelectedProdsQty,
  handleSelectedProdsPurchaseDisc,
  handleDeleteProd,
  handleSelectedProds,
  handleSelectedProdsListPrice,
  form,
  handleAddNewProductByISBN,
  handleUpdateNewProductField,
  allCurrencies,
  allPublishers = [],
  onOpenAddPublisher,
}) {
  // Helper function to get product by key
  const getProductByKey = (key) => selectedProds.find(prod => prod.key === key);

  const scrollContainerRef = useRef(null);
  const prevLenRef = useRef((selectedProds || []).length);

  const dispatch = useDispatch();
  const [addProdVisible, setAddProdVisible] = useState(false);
  const [productSearchModalVisible, setProductSearchModalVisible] = useState(false);
  const [searchValues, setSearchValues] = useState({});
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [publisherSearchByKey, setPublisherSearchByKey] = useState({});

  // Initialize filtered products when allProducts changes
  useEffect(() => {
    if (allProducts) {
      setFilteredProducts(allProducts);
    }
  }, [allProducts]);

  // Auto-scroll to bottom when a new product row is added
  useEffect(() => {
    const len = (selectedProds || []).length;
    const prev = prevLenRef.current;
    prevLenRef.current = len;
    if (len > prev) {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
          // Focus the last autocomplete input if present
          const inputs = scrollContainerRef.current.querySelectorAll('.ant-select-selection-search-input');
          if (inputs.length > 0) inputs[inputs.length - 1].focus();
        }
      }, 120);
    }
  }, [selectedProds]);

  // Debounced search for server-side product search
  const debouncedSearch = useCallback(
    debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.trim() === '') {
        setFilteredProducts(allProducts || []);
        setIsSearching(false);
        return;
      }
      setIsSearching(true);
      try {
        const result = await dispatch(globalProductSearch(searchTerm.trim(), 1, 50));
        if (result && result.status === "success") {
          setFilteredProducts(result.data || []);
        }
      } catch (error) {
        console.error('Search error:', error);
        const filtered = allProducts?.filter(product =>
          product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.isbn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.author?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.book_publisher?.name?.toLowerCase().includes(searchTerm.toLowerCase())
        ) || [];
        setFilteredProducts(filtered);
      } finally {
        setIsSearching(false);
      }
    }, 500),
    [allProducts, dispatch]
  );

  const handleSearch = (value, key) => {
    setSearchValues(prev => ({ ...prev, [key]: value }));
    debouncedSearch(value);
  };

  const checkISBNExists = async (isbn) => {
    if (!isbn || !isbn.trim()) return null;
    try {
      const result = await dispatch(globalProductSearch(isbn.trim(), 1, 10));
      if (result && result.status === "success" && result.data) {
        const exactMatch = result.data.find(p => p.isbn === isbn.trim());
        return exactMatch || null;
      }
      return null;
    } catch (error) {
      console.error('Error checking ISBN:', error);
      return null;
    }
  };

  const handleISBNInput = async (isbn, key, addCallback, fieldName) => {
    if (!isbn || !isbn.trim()) return;
    const existingProduct = await checkISBNExists(isbn);
    if (existingProduct) {
      handleSelectedProds(existingProduct.id, key);
      if (form && fieldName !== undefined) {
        form.setFieldValue(['purchaseInvoiceProduct', fieldName, 'product_id'], existingProduct.id);
      }
      if (addCallback) addCallback();
    } else {
      if (handleAddNewProductByISBN) {
        handleAddNewProductByISBN(isbn, key);
        if (addCallback) addCallback();
        setSearchValues(prev => ({ ...prev, [key]: '' }));
      }
    }
  };
  
  return (
    <>
      {/* Mobile-friendly Add Product Button */}
      <div className="d-lg-none mb-3">
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setProductSearchModalVisible(true)}
          style={{ width: '100%', height: '40px' }}
        >
          Add Product to Bill
        </Button>
      </div>

      {/* Desktop Product Table */}
      <div className="d-none d-lg-block">
      <Row gutter={[24]} >
        <Col span={1}>
          <div className="font-weight-bold border-b">SL</div>
        </Col>
        <Col span={6}>
          <div className="font-weight-bold border-b">ISBN (Title)</div>
        </Col>
        {/* <Col span={3}>
          <div className="font-weight-bold border-b">Title</div>
        </Col> */}
        <Col span={2}>
          <div className="font-weight-bold border-b">Author</div>
        </Col>
        <Col span={2}>
          <div className="font-weight-bold border-b">Publisher</div>
        </Col>
        <Col span={1}>
          <div className="font-weight-bold">Curr</div>
        </Col>
        <Col span={3}>
          <div className="font-weight-bold">List (MRP)</div>
        </Col>
        <Col span={2}>
          <div className="font-weight-bold">Qty</div>
        </Col>
        <Col span={3}>
          <div className="font-weight-bold">Disc(%)</div>
        </Col>
        <Col span={2}>
          <div className="font-weight-bold">Total</div>
        </Col>
        <Col span={2}>
          <div className="font-weight-bold">Action</div>
        </Col>
      </Row>
   
      <hr style={{ backgroundColor: "black" }} />

      <Form.List name="purchaseInvoiceProduct">
        {(fields, { add, remove }) => (
          <>
            <div
              ref={scrollContainerRef}
              style={{
                maxHeight: '500px',
                overflowY: 'auto',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                padding: '8px'
              }}
            >
            {fields.map(({ key, name, ...restField }, index) => (
              <Row className="mt-2 purchase-product-row" align="middle" gutter={[24]} key={key} data-row-index={index}>

              {/*S. No. Start */}
                <Col span={1}>
                  {index + 1}
                </Col>
              {/*S. No. End */}


               {/* ISBN / Product - AutoComplete with Enter to add and product name below; new product shows ISBN + Product Name * */}
               <Col span={6}>
                  {(() => {
                    const product = getProductByKey(key) || (selectedProds && selectedProds.length > index && selectedProds[index]);
                    const isNewProduct = product?.isNewProduct;

                    if (isNewProduct) {
                      return (
                        <div>
                          <Form.Item {...restField} name={[name, "product_id"]} noStyle>
                            <input type="hidden" />
                          </Form.Item>
                          <Input
                            placeholder="ISBN"
                            value={product?.isbn || ""}
                            onChange={(e) => {
                              if (handleUpdateNewProductField) {
                                handleUpdateNewProductField(key, 'isbn', e.target.value);
                              }
                            }}
                            onBlur={async (e) => {
                              const newIsbn = e.target.value?.trim();
                              if (newIsbn && newIsbn !== product?.isbn) {
                                const existingProduct = await checkISBNExists(newIsbn);
                                if (existingProduct) {
                                  handleSelectedProds(existingProduct.id, key);
                                  if (form) form.setFieldValue(['purchaseInvoiceProduct', name, 'product_id'], existingProduct.id);
                                } else if (handleUpdateNewProductField) {
                                  handleUpdateNewProductField(key, 'isbn', newIsbn);
                                }
                              }
                            }}
                            size="small"
                            style={{ marginBottom: '4px' }}
                          />
                          <Form.Item
                            {...restField}
                            name={[name, "product_name"]}
                            rules={[
                              {
                                required: true,
                                message: 'Product Name is required',
                                validator: (_, value) => {
                                  const p = getProductByKey(key);
                                  if (p?.isNewProduct) {
                                    const nameVal = value || p?.name || '';
                                    if (!nameVal || !nameVal.trim()) {
                                      return Promise.reject(new Error('Product Name is required'));
                                    }
                                  }
                                  return Promise.resolve();
                                }
                              }
                            ]}
                            style={{ marginBottom: 0 }}
                            initialValue={product?.name || ""}
                          >
                            <Input
                              placeholder="Product Name *"
                              value={product?.name || ""}
                              onChange={(e) => {
                                if (handleUpdateNewProductField) {
                                  handleUpdateNewProductField(key, 'name', e.target.value);
                                }
                                if (form) {
                                  form.validateFields([['purchaseInvoiceProduct', name, 'product_name']]);
                                }
                              }}
                              onBlur={() => {
                                if (form) form.validateFields([['purchaseInvoiceProduct', name, 'product_name']]);
                              }}
                              size="small"
                            />
                          </Form.Item>
                        </div>
                      );
                    }

                    const formProductId = form?.getFieldValue(['purchaseInvoiceProduct', name, 'product_id']);
                    const resolvedProduct = product || (formProductId && allProducts?.find(p => p.id === formProductId));
                    let isbnValue = resolvedProduct?.isbn || '';
                    let productName = resolvedProduct?.name || '';
                    if (resolvedProduct && !resolvedProduct.isbn && resolvedProduct.id) {
                      const fromStore = allProducts?.find(p => p.id === resolvedProduct.id);
                      if (fromStore) {
                        isbnValue = fromStore.isbn || '';
                        productName = fromStore.name || '';
                      }
                    }
                    if (searchValues[key]) {
                      isbnValue = searchValues[key];
                      productName = '';
                    }
                    return (
                      <>
                        <Form.Item {...restField} name={[name, "product_id"]} noStyle>
                          <input type="hidden" />
                        </Form.Item>
                        <div style={{ position: 'relative' }}>
                          <AutoComplete
                            placeholder={isbnValue && !searchValues[key] ? '' : 'Enter ISBN or Search'}
                            value={searchValues[key] ?? ''}
                            style={{
                              width: '100%',
                              minHeight: (productName && !searchValues[key]) ? '48px' : '32px'
                            }}
                            className="product-isbn-autocomplete-multiline"
                            suffixIcon={null}
                            options={(() => {
                              const currentOptions = searchValues[key] ? filteredProducts : allProducts;
                              const productOptions = (currentOptions?.slice(0, 10) || []).map((p) => ({
                                value: p.isbn,
                                label: (
                                  <div style={{ lineHeight: '1.2' }}>
                                    <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{p.isbn}</div>
                                    <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{p.name}</div>
                                  </div>
                                )
                              }));
                              if (searchValues[key] && searchValues[key].trim()) {
                                return [
                                  ...productOptions,
                                  {
                                    value: '__add_new_product__',
                                    label: (
                                      <div style={{
                                        lineHeight: '1.2',
                                        color: '#1890ff',
                                        fontWeight: 'bold',
                                        padding: '4px 0',
                                        borderTop: '1px solid #f0f0f0',
                                        marginTop: '4px'
                                      }}>
                                        <PlusOutlined /> Add New Product: "{searchValues[key]}"
                                      </div>
                                    )
                                  }
                                ];
                              }
                              return productOptions;
                            })()}
                            onSearch={(value) => {
                              setSearchValues(prev => ({ ...prev, [key]: value }));
                              debouncedSearch(value);
                            }}
                            onSelect={(selectedValue) => {
                              if (selectedValue === '__add_new_product__') {
                                setAddProdVisible(true);
                                setSearchValues(prev => ({ ...prev, [key]: '' }));
                              } else {
                                const selectedProduct = allProducts?.find(p => p.isbn === selectedValue) ||
                                  filteredProducts?.find(p => p.isbn === selectedValue);
                                if (selectedProduct) {
                                  handleSelectedProds(selectedProduct.id, key);
                                  if (form) {
                                    form.setFieldValue(['purchaseInvoiceProduct', name, 'product_id'], selectedProduct.id);
                                  }
                                  add();
                                  setSearchValues(prev => ({ ...prev, [key]: '' }));
                                  setFilteredProducts(allProducts || []);
                                }
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const value = e.target.value?.trim();
                                if (value && !resolvedProduct) {
                                  if (/^[A-Z0-9-]+$/i.test(value)) {
                                    handleISBNInput(value, key, add, name);
                                  }
                                }
                              }
                            }}
                            onBlur={async (e) => {
                              const value = e.target.value?.trim();
                              if (value && !resolvedProduct) {
                                if (/^[A-Z0-9-]+$/i.test(value)) {
                                  await handleISBNInput(value, key, add, name);
                                }
                              }
                            }}
                            filterOption={false}
                            notFoundContent={
                              searchValues[key] && filteredProducts.length === 0 && !isSearching ? (
                                <div style={{ textAlign: 'center', padding: '10px' }}>
                                  <div style={{ marginBottom: '8px' }}>No products found for "{searchValues[key]}"</div>
                                  <Button
                                    type="primary"
                                    size="small"
                                    icon={<PlusOutlined />}
                                    onClick={() => {
                                      setAddProdVisible(true);
                                    }}
                                  >
                                    + Add New Product
                                  </Button>
                                </div>
                              ) : null
                            }
                          />
                          {!searchValues[key] && isbnValue && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '1px',
                                left: '11px',
                                right: '32px',
                                padding: '4px 0',
                                pointerEvents: 'none',
                                zIndex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                minHeight: productName ? '44px' : '24px',
                                lineHeight: '1.5'
                              }}
                            >
                              <div style={{ fontWeight: '500', fontSize: '13px', color: '#000' }}>{isbnValue}</div>
                              {productName && (
                                <div style={{
                                  fontSize: '11px',
                                  color: '#666',
                                  marginTop: '2px',
                                  lineHeight: '1.3',
                                  wordWrap: 'break-word',
                                  whiteSpace: 'normal'
                                }}>
                                  {productName}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </Col>
               {/* ISBN End */}

               {/* Title Start */}
                {/* <Col span={3}>
                  <Form.Item {...restField} name={[name, "product_id"]}>
                    <Select
                      placeholder="Select Product"
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onChange={(prodId) => handleSelectedProds(prodId, key)}
                    >
                      {Array.isArray(allProducts) &&
                        allProducts.map((p) => (
                          <Select.Option key={p.id} value={p.id}>
                            {p.name}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col> */}
                {/* Title Start */}

                {/*Author Starts*/}
                <Col span={2}>
                  {/* <Form.Item {...restField}  name={[name, "product_purchase_price"]}   >
                    <Input
                      style={{ width: "100%" }}
                      placeholder="Purchase price"
                      onChange={(purchasePrice) =>
                        handleSelectedProdsPurchasePrice(key, purchasePrice)
                      }
                      value={
                        selectedProds[key]
                          ? selectedProds[key].author
                          : ""
                      }
                      readOnly
                    />
                    <p style={{ display: "none" }}>
                      {selectedProds[key]
                        ? selectedProds[key].author
                        : ""}
                    </p>
                  </Form.Item> */}

                  {getProductByKey(key)?.isNewProduct ? (
                    <Input
                      placeholder="Author"
                      value={getProductByKey(key)?.author || ""}
                      onChange={(e) => handleUpdateNewProductField && handleUpdateNewProductField(key, 'author', e.target.value)}
                      size="small"
                    />
                  ) : (
                    <div className="font-weight-bold">
                      {getProductByKey(key)?.author || ""}
                    </div>
                  )}
                </Col>
                {/*Author Ends*/}

                {/*Publisher Starts*/}
                <Col span={2}>
                  {getProductByKey(key)?.isNewProduct || !getProductByKey(key)?.book_publisher ? (
                    <Select
                      size="small"
                      placeholder="Publisher"
                      showSearch
                      allowClear
                      value={getProductByKey(key)?.book_publisher_id || getProductByKey(key)?.book_publisher?.id || undefined}
                      onSearch={(val) => setPublisherSearchByKey((prev) => ({ ...prev, [key]: val }))}
                      onChange={(publisherId) => {
                        const publisher = allPublishers.find((p) => p.id === publisherId);
                        if (handleUpdateNewProductField && publisher) {
                          handleUpdateNewProductField(key, "book_publisher", publisher);
                        }
                      }}
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          {publisherSearchByKey[key] != null &&
                            publisherSearchByKey[key].trim() !== "" &&
                            !allPublishers.some(
                              (p) =>
                                (p.name || "").toLowerCase().trim() ===
                                (publisherSearchByKey[key] || "").toLowerCase().trim()
                            ) &&
                            onOpenAddPublisher && (
                              <div style={{ padding: "8px", borderTop: "1px solid #f0f0f0" }}>
                                <Button
                                  type="link"
                                  icon={<PlusOutlined />}
                                  onClick={() => {
                                    onOpenAddPublisher(key, (publisherSearchByKey[key] || "").trim());
                                  }}
                                  style={{ width: "100%", textAlign: "left" }}
                                >
                                  Add &quot;{publisherSearchByKey[key]}&quot; from here
                                </Button>
                              </div>
                            )}
                        </>
                      )}
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes((input || "").toLowerCase())
                      }
                      options={allPublishers.map((p) => ({ value: p.id, label: p.name }))}
                      style={{ width: "100%" }}
                    />
                  ) : (
                    <div className="font-weight-bold">
                      {getProductByKey(key)?.book_publisher?.name ||
                        (getProductByKey(key)?.book_publisher_id &&
                          allPublishers.find((p) => p.id === getProductByKey(key).book_publisher_id)?.name) ||
                        ""}
                    </div>
                  )}
                </Col>
                {/*Publisher Ends*/}

                {/*Curr Starts*/}
                <Col span={1}>
                {/* <Form.Item
                    {...restField}
                    name={[name, "product_purchase_price"]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="Curr"
                      onChange={(purchasePrice) =>
                        handleSelectedProdsPurchasePrice(key, purchasePrice)
                      }
                      value={
                        selectedProds[key]
                          ? selectedProds[key].product_currency.symbol
                          : ""
                      }
                      readOnly
                    />
                    <p style={{ display: "none" }}>
                      {selectedProds[key]
                        ? selectedProds[key].product_currency.symbol
                        : ""}
                    </p>
                  </Form.Item> */}
                  {getProductByKey(key)?.isNewProduct && allCurrencies ? (
                    <Select
                      size="small"
                      value={getProductByKey(key)?.product_currency?.id || allCurrencies[0]?.id}
                      onChange={(currencyId) => {
                        const selectedCurrency = allCurrencies.find(c => c.id === currencyId);
                        if (handleUpdateNewProductField && selectedCurrency) {
                          handleUpdateNewProductField(key, 'product_currency', selectedCurrency);
                        }
                      }}
                      style={{ width: '100%' }}
                    >
                      {allCurrencies.map((c) => (
                        <Select.Option key={c.id} value={c.id}>{c.symbol}</Select.Option>
                      ))}
                    </Select>
                  ) : (
                    <div className="font-weight-bold">
                      {getProductByKey(key)?.product_currency?.symbol || ""}
                    </div>
                  )}
                </Col>
                {/*Curr Ends*/}

                {/* List / MRP (line amount & master list price sync); hidden purchase field must stay inside Col so Row grid is not broken */}
                <Col span={3}>
                  <Form.Item {...restField} name={[name, "product_purchase_price"]} noStyle>
                    <InputNumber style={{ display: "none" }} tabIndex={-1} aria-hidden />
                  </Form.Item>
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="List / MRP"
                    min={0}
                    value={getProductByKey(key)?.sale_price ?? ""}
                    onChange={(v) =>
                      handleSelectedProdsListPrice &&
                      handleSelectedProdsListPrice(key, v, name)
                    }
                    size="small"
                  />
                </Col>

                {/*Qty Starts*/}
                <Col span={2}>
                  <Form.Item {...restField} name={[name, "product_quantity"]}>
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="Product Quantity"
                      onChange={(qty) => handleSelectedProdsQty(key, qty)}
                      value={
                        getProductByKey(key)?.selectedQty || ""
                      }
                    />
                    <p style={{ display: "none" }}>
                      {selectedProds[key] ? selectedProds[key].selectedQty : ""}
                    </p>
                  </Form.Item>
                </Col>
                {/*Qty Ends*/}

                {/* Product Discount */}
                <Col span={3}>
                  <Form.Item {...restField} name={[name, "product_purchase_discount"]}>
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="Disc"
                      onChange={(purchaseDisc) => handleSelectedProdsPurchaseDisc(key, purchaseDisc)
                      }
                      value={
                        getProductByKey(key)?.purchase_disc || ""
                      }
                      min={0}
										  max={100}
                    />
                    <p style={{ display: "none" }}>
                      {selectedProds[key] ? selectedProds[key].purchase_disc : ""}
                    </p>
                  </Form.Item>
                </Col>
                  {/* {console.log("discount",selectedProds[key].selectedDisc)} */}

                {/* Discount Ends */}
                {/*Total Starts*/}
                <Col span={2}>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <div className="font-weight-bold">
                      {(() => {
                        const product = getProductByKey(key);
                        if (!product) return "0.00";
                        const conversion = product.product_currency?.conversion ?? 1;
                        const price = product.sale_price ?? product.purchase_price ?? 0;
                        const qty = product.selectedQty ?? 1;
                        const disc = product.purchase_disc ?? 0;
                        const total = (qty * (price * conversion)) - (qty * (price * conversion) * (disc / 100));
                        return (total || 0).toFixed(2);
                      })()}
                    </div>
                  </Form.Item>
                </Col>
                {/*Total Ends*/}

                 {/*Delete Starts*/}
                 {/* <Col span={2}>
                  <Form.Item>
                    <Button style={{ width:'100% !important'}}
                      shape="circle"
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        remove(name);
                        handleDeleteProd(key);
                      }}
                    ></Button>
                  </Form.Item>
                </Col> */}
                <Col
                  span={2}
                  className="purchase-row-action-col"
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                  }}
                >
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined style={{ fontSize: 18 }} />}
                      onClick={() => {
                        remove(name);
                        handleDeleteProd(key);
                      }}
                      aria-label="Remove line"
                      style={{ minWidth: 36, minHeight: 36, padding: "4px 8px" }}
                    />
                  </Form.Item>
                </Col>

                {/*Delete Ends*/}
              </Row>
            ))}
            </div>
            <Form.Item style={{ marginTop: "20px" }}>
              <Button 
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                Add
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
      </div>

      {/* Mobile Product List */}
      <div className="d-lg-none">
        {selectedProds && selectedProds.length > 0 ? (
          <div className="row">
            {selectedProds.map((product, index) => (
              <div key={index} className="col-12 mb-3">
                <Card size="small">
                  {/* Product Information */}
                  <div className="mb-3">
                    <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: '8px' }}>
                      {product.name}
                    </Text>
                    <div className="row">
                      <div className="col-6">
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                          <strong>ISBN:</strong> {product.isbn}
                        </Text>
                      </div>
                      <div className="col-6">
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                          <strong>Author:</strong> {product.author || 'N/A'}
                        </Text>
                      </div>
                    </div>
                    <div className="row mt-1">
                      <div className="col-6">
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                          <strong>Publisher:</strong> {product.book_publisher?.name || 'N/A'}
                        </Text>
                      </div>
                      <div className="col-6">
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                          <strong>Currency:</strong> {product.product_currency?.symbol || '₹'}
                        </Text>
                      </div>
                    </div>
                  </div>

                  {/* Editable Fields */}
                  <div className="row">
                    <div className="col-12">
                      <Text style={{ fontSize: '12px', fontWeight: 'bold' }}>List / MRP:</Text>
                      <InputNumber
                        min={0}
                        value={product.sale_price ?? 0}
                        onChange={(value) =>
                          handleSelectedProdsListPrice &&
                          handleSelectedProdsListPrice(product.key, value)
                        }
                        style={{ width: '100%' }}
                        size="small"
                      />
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col-6">
                      <Text style={{ fontSize: '12px', fontWeight: 'bold' }}>Discount(%):</Text>
                      <InputNumber
                        min={0}
                        max={100}
                        value={product.purchase_disc || 0}
                        onChange={(value) => handleSelectedProdsPurchaseDisc(product.key, value)}
                        style={{ width: '100%' }}
                        size="small"
                      />
                    </div>
                    <div className="col-6" />
                  </div>

                  {/* Quantity and Actions */}
                  <div className="row mt-2">
                    <div className="col-6">
                      <Text style={{ fontSize: '12px', fontWeight: 'bold' }}>Quantity:</Text>
                      <InputNumber
                        min={1}
                        value={product.selectedQty || 1}
                        onChange={(value) => handleSelectedProdsQty(product.key, value)}
                        style={{ width: '100%' }}
                        size="small"
                      />
                    </div>
                    <div className="col-6 d-flex align-items-end">
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteProd(product.key)}
                        size="small"
                        style={{ marginLeft: 'auto' }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>

                  {/* Total Amount Display */}
                  <div className="mt-2 pt-2" style={{ borderTop: '1px solid #f0f0f0' }}>
                    <div className="d-flex justify-content-between">
                      <Text style={{ fontSize: '12px' }}>Total Amount:</Text>
                      <Text strong style={{ fontSize: '14px' }}>
                        {product.product_currency?.symbol || '₹'}
                        {(() => {
                          const total = (product.selectedQty || 1) *
                            ((product.sale_price ?? product.purchase_price ?? 0) *
                            (product.product_currency?.conversion || 1));
                          const discount = total * ((product.purchase_disc || 0) / 100);
                          return (total - discount).toFixed(2);
                        })()}
                      </Text>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4">
            <Text type="secondary">No products added yet</Text>
          </div>
        )}
      </div>

      {/* Product Search Modal */}
      <ProductSearchModal
        visible={productSearchModalVisible}
        onClose={() => setProductSearchModalVisible(false)}
        allProducts={allProducts}
        selectedProds={selectedProds}
        onProductSelect={(product) => {
          const nextKey = selectedProds.length;
          handleSelectedProds(product.id, nextKey);
          setProductSearchModalVisible(false);
        }}
        onAddNewProduct={() => {
          setProductSearchModalVisible(false);
          setAddProdVisible(true);
        }}
      />

      {/* Add Product Modal */}
      <Modal
        title="Add New Product"
        open={addProdVisible}
        onCancel={() => setAddProdVisible(false)}
        footer={null}
        width={1200}
        style={{ top: 20 }}
        bodyStyle={{ paddingTop: 12 }}
        destroyOnClose={true}
      >
        <AddProd 
          onClose={() => {
            setAddProdVisible(false);
            window.location.reload();
          }}
        />
      </Modal>
    </>
  );
}
